/**
 * The identity line above the chart: what you are looking at, at what price,
 * and how long the current bar has left.
 *
 * Everything here answers a question you would otherwise have to hold in your
 * head. The exchange and company name say the ticker is the one you meant;
 * the bid and ask say what you would actually pay, which a last price cannot;
 * and the countdown says whether the rightmost candle is finished or still
 * being written, which changes whether you should act on its shape at all.
 */
import { formatNum, formatPrice, formatVolume } from "@tt/shared";
import { useEffect, useState } from "react";

import { FlashValue } from "./FlashValue";

export interface SymbolHeaderBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface SymbolHeaderProps {
  symbol: string;
  name?: string | null;
  exchange?: string | null;
  timeframe: string;
  price: number | null;
  change: number | null;
  changePct: number | null;
  bid?: number | null;
  ask?: number | null;
  bar?: SymbolHeaderBar | null;
  /** Seconds per bar, for the countdown. 0 or undefined hides it. */
  barSeconds?: number;
  children?: React.ReactNode;
}

export function SymbolHeader({
  symbol, name, exchange, timeframe, price, change, changePct,
  bid, ask, bar, barSeconds, children,
}: SymbolHeaderProps) {
  const tone = change === null ? "flat" : change > 0 ? "up" : change < 0 ? "down" : "flat";

  return (
    <div className="quote-head">
      <span className="sym">{symbol}</span>
      {name && <span className="sym-name" title={name}>{name}</span>}
      <span className="sym-meta">
        {timeframe}
        {exchange && <> · {exchange}</>}
      </span>

      <FlashValue value={price} className="px">
        {price === null ? "—" : formatPrice(price)}
      </FlashValue>
      <span className={`chg ${tone}`}>
        {change === null || changePct === null
          ? ""
          : `${change >= 0 ? "+" : ""}${formatNum(change)} (${changePct.toFixed(2)}%)`}
      </span>

      {bid != null && ask != null && (
        // Spread is shown as a number rather than left to be subtracted: it is
        // the cost of a round trip, and on a wide one that cost dwarfs the
        // move anyone is trying to capture.
        <span className="bidask" title={`Spread ${formatNum(ask - bid)}`}>
          <span className="ba-bid">{formatNum(bid)}</span>
          <span className="ba-spread">{formatNum(ask - bid)}</span>
          <span className="ba-ask">{formatNum(ask)}</span>
        </span>
      )}

      {bar && <OHLC bar={bar} />}
      {barSeconds ? <BarCountdown bar={bar} seconds={barSeconds} /> : null}

      {children}
    </div>
  );
}

/** O/H/L/C tinted against the bar's own open, which is what makes a red or
 *  green candle -- not against the previous close. */
function OHLC({ bar }: { bar: SymbolHeaderBar }) {
  const tone = bar.close >= bar.open ? "up" : "down";
  return (
    <span className="ohlc">
      <span className="k">O</span><span className={tone}>{formatNum(bar.open)}</span>
      <span className="k">H</span><span className={tone}>{formatNum(bar.high)}</span>
      <span className="k">L</span><span className={tone}>{formatNum(bar.low)}</span>
      <span className="k">C</span><span className={tone}>{formatNum(bar.close)}</span>
      <span className="k">V</span><span className="v">{formatVolume(bar.volume)}</span>
    </span>
  );
}

/**
 * Time left in the bar being formed.
 *
 * Bars are stamped at their opening instant, so the close is start + width.
 * A negative remainder means the feed has not yet delivered the next bar --
 * common at the open, and on a delayed feed all the time -- so it reads as
 * waiting rather than counting past zero into nonsense.
 */
function BarCountdown({ bar, seconds }: { bar?: SymbolHeaderBar | null; seconds: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Aligned to the second so the digits change when a wall clock would.
    let timer: number;
    const tick = () => {
      setNow(Date.now());
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
    };
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
    return () => window.clearTimeout(timer);
  }, []);

  if (!bar) return null;
  const closesAt = (bar.time + seconds) * 1000;
  const left = Math.floor((closesAt - now) / 1000);
  if (left < 0) return <span className="countdown waiting" title="Waiting for the next bar">—</span>;

  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    <span
      className={`countdown ${left <= 10 ? "soon" : ""}`}
      title="Time left in the current bar"
    >
      {h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`}
    </span>
  );
}
